import { useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { PoolMembership } from "../types/pools";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";

type UsePoolsArgs = {
  enabled: boolean;
};

const parseApiError = async (response: Response, fallback: string) => {
  const errorText = await response.text();
  if (!errorText) return fallback;

  try {
    const parsed = JSON.parse(errorText) as { detail?: unknown; message?: unknown };
    if (typeof parsed.detail === "string") {
      return parsed.detail;
    }
    if (parsed.detail !== undefined) {
      return JSON.stringify(parsed.detail);
    }
    if (typeof parsed.message === "string") {
      return parsed.message;
    }
  } catch {
    // Keep raw response text when payload is not JSON.
  }

  return errorText;
};

export function usePools({ enabled }: UsePoolsArgs) {
  const { isSignedIn: isAuthenticated, user } = useUser();
  const { acquireToken } = useApiAuth();
  const canLoad = enabled && isAuthenticated;

  const query = useQuery({
    queryKey: ["pools", user?.id],
    enabled: canLoad && Boolean(user?.id),
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to load pools.");
      }

      const response = await fetch(apiUrl("/api/pools"), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const detail = await parseApiError(
          response,
          `Failed to load pools (${response.status})`,
        );
        throw new Error(`Failed to load pools (${response.status}): ${detail || "No error detail returned"}`);
      }

      const payload: PoolMembership[] = await response.json();
      return payload;
    },
  });

  const memberships = useMemo(() => (canLoad ? query.data ?? [] : []), [canLoad, query.data]);

  return {
    memberships,
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: async () => {
      await query.refetch();
    },
  };
}

export type UsePoolsResult = ReturnType<typeof usePools>;
